import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useModal } from 'react-native-modalfy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { IconTouchable } from '../../common/IconTouchable';
import {
  OptionMenuOptionsType,
  OptionsMenuPopover,
} from '../../views/OptionsMenuPopover';
import { IS_MOBILE, IS_WEB, parseApiError } from '@utils';
import { usePopover } from '@hooks';
import { modals, ModalStackParamsType } from '@modals';
import { Post } from '@models/Posts/PostModel';
import { MainStackParamListType, screens } from '@navigation';
import { ToastService } from '@services';

export interface IPostActionMenuProps {
  item: Post;
}

type NavigationPropType =
  NativeStackNavigationProp<MainStackParamListType>;

export const PostActionMenu = observer<IPostActionMenuProps>(
  ({ item }) => {
    const { t } = useTranslation();
    const popover = usePopover();
    const modal = useModal<ModalStackParamsType>();
    const nav = useNavigation<NavigationPropType>();

    function openMenu() {
      if (IS_WEB) {
        popover.togglePopover();
      } else {
        modal.openModal(modals.OptionsMenuModal, {
          options,
        });
      }
    }

    function navigateToReport() {
      if (IS_WEB) {
        modal.openModal(modals.ReportModal, {
          item,
        });
      } else {
        nav.navigate(screens.Report, {
          item,
        });
      }
    }

    function navigateToPostEditor() {
      if (IS_WEB) {
        modal.openModal(modals.PostEditorModal, {
          item,
        });
      } else {
        nav.navigate(screens.PostNavigator, {
          screen: screens.PostEditor,
          params: {
            item,
          },
        });
      }
    }

    function openRemoveModal() {
      modal.openModal(modals.ConfirmationModal, {
        title: t('common.postItem.deleteComment'),
        subtitle: t('common.postItem.deleteSubtitle'),
        buttons: [
          { text: t('common.postItem.delete') },
          { text: t('common.postItem.cancel') },
        ],
        onConfirm: () => {
          remove();
          if (IS_MOBILE) {
            // Also close options modal
            modal.closeAllModals();
          } else {
            modal.closeModal();
          }
        },
      });
    }

    async function remove() {
      try {
        await item.remove.run();

        ToastService.showSuccess(t('common.postItem.successMessage'));
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    const options = useMemo(() => {
      const values: OptionMenuOptionsType = [];

      if (item.canBeReported) {
        values.push({
          label: t('common.postItem.report'),
          icon: 'danger-light',
          onPress: navigateToReport,
        });
      }

      if (item.user?.isViewer) {
        values.push(
          {
            label: t('common.postItem.edit'),
            icon: 'edit-light',
            onPress: navigateToPostEditor,
          },
          {
            label: t('common.postItem.delete'),
            icon: 'delete-light',
            onPress: openRemoveModal,
            color: '$error300',
          },
        );
      }

      return values;
    }, [item.user?.isViewer]);

    if (options.length === 0) {
      return null;
    }

    return (
      <View>
        <OptionsMenuPopover ref={popover.ref} items={options}>
          <IconTouchable
            size={20}
            name="point-vertical"
            onPress={openMenu}
          />
        </OptionsMenuPopover>
      </View>
    );
  },
);
